import type { ApiBody, ApiQuery } from '../../../shared/api/contracts'
import { apiRequest, apiGet, apiPost, apiPut, apiDelete } from './transport'
import { financialQuery } from './financialQuery'

export async function fetchDebts(filters?: ApiQuery<'GET /admin/debts'>, signal?: AbortSignal) {
  const u = new URLSearchParams()
  if (filters?.status) u.set('status', filters.status)
  if (filters?.type) u.set('type', filters.type)
  if (filters?.includeClosed) u.set('includeClosed', 'true')
  if (filters?.search) u.set('search', filters.search)
  const qs = u.toString()
  return apiGet<'GET /admin/debts'>(`/admin/debts${qs ? `?${qs}` as const : ''}`, signal)
}

export async function fetchDebt(id: string, signal?: AbortSignal) {
  return apiGet<'GET /admin/debts/:id'>(`/admin/debts/${encodeURIComponent(id)}`, signal)
}

export async function createDebt(payload: ApiBody<'POST /admin/debts'>, signal?: AbortSignal) {
  return apiPost<'POST /admin/debts'>('/admin/debts', payload, signal)
}

export async function updateDebt(id: string, patch: ApiBody<'PUT /admin/debts/:id'>, signal?: AbortSignal) {
  return apiPut<'PUT /admin/debts/:id'>(`/admin/debts/${encodeURIComponent(id)}`, patch, signal)
}

export async function deleteDebt(id: string, signal?: AbortSignal) {
  return apiDelete<'DELETE /admin/debts/:id'>(`/admin/debts/${encodeURIComponent(id)}`, signal)
}

export async function closeDebt(id: string, signal?: AbortSignal, payload?: ApiBody<'POST /admin/debts/:id/close'>) {
  return apiPost<'POST /admin/debts/:id/close'>(`/admin/debts/${encodeURIComponent(id)}/close`, payload, signal)
}

export async function fetchDebtPayments(debtId: string, signal?: AbortSignal) {
  return apiGet<'GET /admin/debts/:id/payments'>(`/admin/debts/${encodeURIComponent(debtId)}/payments`, signal)
}

export async function createDebtPayment(debtId: string, payload: ApiBody<'POST /admin/debts/:id/payments'>, signal?: AbortSignal) {
  return apiPost<'POST /admin/debts/:id/payments'>(`/admin/debts/${encodeURIComponent(debtId)}/payments`, payload, signal)
}

export async function updateDebtPayment(debtId: string, paymentId: string, patch: ApiBody<'PUT /admin/debts/:id/payments/:paymentId'>, signal?: AbortSignal) {
  return apiPut<'PUT /admin/debts/:id/payments/:paymentId'>(`/admin/debts/${encodeURIComponent(debtId)}/payments/${encodeURIComponent(paymentId)}`, patch, signal)
}

export async function markDebtPaymentPaid(
  debtId: string,
  paymentId: string,
  payload: ApiBody<'POST /admin/debts/:id/payments/:paymentId/pay'>, signal?: AbortSignal
) {
  return apiPost<'POST /admin/debts/:id/payments/:paymentId/pay'>(
    `/admin/debts/${encodeURIComponent(debtId)}/payments/${encodeURIComponent(paymentId)}/pay`,
    payload, signal
  )
}

export async function fetchUpcomingDebtPayments(params?: ApiQuery<'GET /admin/debts/upcoming'>, signal?: AbortSignal) {
  const u = new URLSearchParams()
  if (params?.from) u.set('from', params.from)
  if (params?.to) u.set('to', params.to)
  if (params?.limit != null) u.set('limit', String(params.limit))
  const qs = u.toString()
  return apiGet<'GET /admin/debts/upcoming'>(`/admin/debts/upcoming${qs ? `?${qs}` as const : ''}`, signal)
}

export async function fetchDebtsSummary(params?: ApiQuery<'GET /admin/debts/summary'>, signal?: AbortSignal) {
  const u = new URLSearchParams()
  if (params?.month) u.set('month', params.month)
  const qs = u.toString()
  return apiGet<'GET /admin/debts/summary'>(`/admin/debts/summary${qs ? `?${qs}` as const : ''}`, signal)
}

export async function fetchDebtReminderSettings(debtId: string, signal?: AbortSignal) {
  return apiGet<'GET /admin/debts/:id/reminders'>(
    `/admin/debts/${encodeURIComponent(debtId)}/reminders`, signal
  )
}

export async function updateDebtReminderSettings(debtId: string, payload: ApiBody<'PUT /admin/debts/:id/reminders'>, signal?: AbortSignal) {
  return apiPut<'PUT /admin/debts/:id/reminders'>(
    `/admin/debts/${encodeURIComponent(debtId)}/reminders`,
    payload, signal
  )
}

export function fetchDebtsOverview(params: ApiQuery<'GET /admin/debts/overview'>, signal?: AbortSignal) {
  return apiRequest<'GET /admin/debts/overview'>(`/admin/debts/overview?${financialQuery(params)}`, { signal })
}

export function fetchDebtPage(params: ApiQuery<'GET /admin/debts/page'>, signal?: AbortSignal) {
  return apiRequest<'GET /admin/debts/page'>(`/admin/debts/page?${financialQuery(params)}`, { signal })
}

export function fetchDebtDetails(id: string, params: ApiQuery<'GET /admin/debts/:id/overview'>, signal?: AbortSignal) {
  return apiRequest<'GET /admin/debts/:id/overview'>(`/admin/debts/${encodeURIComponent(id)}/overview?${financialQuery(params)}`, { signal })
}
